/**
 * @file AdminLayout.jsx
 *
 * Admin /cx/* shell. Persistent left sidebar on desktop, drawer on mobile.
 */
import React, { useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  CalendarDays,
  Stethoscope,
  History,
  Bell,
  Gift,
  Settings,
  Menu as MenuIcon,
  X,
  LogOut,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { ADMIN_STRINGS } from "../constants/strings";
import "./AdminLayout.css";

const NAV_ITEMS = [
  { label: ADMIN_STRINGS.NAV_DASHBOARD, to: "/cx", Icon: LayoutDashboard, exact: true },
  { label: ADMIN_STRINGS.NAV_USERS, to: "/cx/usuarios", Icon: Users },
  { label: ADMIN_STRINGS.NAV_APPOINTMENTS, to: "/cx/citas", Icon: CalendarDays },
  { label: ADMIN_STRINGS.NAV_PROFESSIONALS, to: "/cx/profesionales", Icon: Stethoscope },
  { label: ADMIN_STRINGS.NAV_AUDIT, to: "/cx/auditoria", Icon: History },
  { label: ADMIN_STRINGS.NAV_NOTIFICATIONS, to: "/cx/notificaciones", Icon: Bell },
  { label: ADMIN_STRINGS.NAV_REFERRALS, to: "/cx/referidos", Icon: Gift },
  { label: ADMIN_STRINGS.NAV_SETTINGS, to: "/cx/configuracion", Icon: Settings },
];

function isActive(path, item) {
  if (item.exact) return path === item.to;
  return path === item.to || path.startsWith(`${item.to}/`);
}

function SidebarBody({ user, onItemClick, onLogout }) {
  const location = useLocation();
  return (
    <>
      <div className="ax-sidebar-head">
        <span className="ax-eyebrow">Admin</span>
        <span className="ax-title">{ADMIN_STRINGS.TITLE}</span>
      </div>
      <nav className="ax-nav">
        {NAV_ITEMS.map((item) => {
          const Icon = item.Icon;
          const active = isActive(location.pathname, item);
          return (
            <RouterLink
              key={item.to}
              to={item.to}
              className={`ax-nav-item ${active ? "ax-nav-item-active" : ""}`}
              onClick={onItemClick}
            >
              <Icon size={16} />
              <span>{item.label}</span>
            </RouterLink>
          );
        })}
      </nav>
      <div className="ax-sidebar-foot">
        {user?.email && <div className="ax-user">{user.email}</div>}
        <button type="button" className="ax-logout" onClick={onLogout}>
          <LogOut size={14} /> Cerrar sesión
        </button>
      </div>
    </>
  );
}

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    setMobileOpen(false);
    await logout();
    navigate("/login");
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="ax-shell">
      <aside className="ax-sidebar">
        <SidebarBody user={user} onItemClick={() => {}} onLogout={handleLogout} />
      </aside>

      <main className="ax-main">{children}</main>

      <button
        type="button"
        className="ax-fab"
        onClick={() => setMobileOpen(true)}
        aria-label="Abrir navegación"
      >
        <MenuIcon size={20} />
      </button>

      {mobileOpen && (
        <div className="ax-mobile-overlay" onClick={closeMobile}>
          <aside className="ax-sidebar ax-sidebar-mobile" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="ax-mobile-close"
              onClick={closeMobile}
              aria-label="Cerrar navegación"
            >
              <X size={18} />
            </button>
            <SidebarBody user={user} onItemClick={closeMobile} onLogout={handleLogout} />
          </aside>
        </div>
      )}
    </div>
  );
}
